/* platform/SystemHealth — Backend health dashboard. Auto-refreshes every 30s. */
import React, { useState, useEffect, useCallback } from 'react';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill, Btn } from '../shared/ui';
import { PageHead, StatCard } from './_ui';
import { get } from '../api/client.js';

const fmt = (bytes) => {
  if (bytes == null) return '—';
  const mb = bytes / (1024 * 1024);
  return mb > 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb.toFixed(0) + ' MB';
};

const fmtUptime = (sec) => {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm';
};

const statusTone = (s) => ({ healthy: 'green', degraded: 'amber', down: 'red', not_configured: 'gray' }[s] || 'gray');
const statusLabel = (s) => ({ healthy: 'Healthy', degraded: 'Degraded', down: 'Down', not_configured: 'Not Configured' }[s] || s);

const SERVICE_ICONS = {
  api: 'Globe', database: 'Database', redis: 'Layers', queue: 'ListOrdered',
  smtp: 'Mail', storage: 'HardDrive', workers: 'Cpu',
};

const SERVICE_LABELS = {
  api: 'API', database: 'Database', redis: 'Redis', queue: 'Queue',
  smtp: 'SMTP', storage: 'Storage', workers: 'Workers',
};

const getDiskColor = (pct) => {
  if (pct > 85) return '#DC2626';
  if (pct > 70) return '#E08A00';
  return '#0E8C5A';
};

const getServiceIconColor = (tone) => {
  if (tone === 'green') return 'var(--nx-success)';
  if (tone === 'amber') return '#E08A00';
  if (tone === 'red')   return 'var(--nx-danger)';
  return 'var(--nx-text-muted)';
};

const getBannerTitle = (loading, allOk) => {
  if (loading) return 'Checking system status…';
  return allOk ? 'All systems operational' : 'Some services require attention';
};

const ServiceCard = ({ name, info }) => {
  const tone = statusTone(info?.status);
  return (
    <div className="nx-cc-chart-card nx-health-service-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ color: getServiceIconColor(tone) }}>
          <NexusIcon name={SERVICE_ICONS[name] || 'Server'} size={20} />
        </span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{SERVICE_LABELS[name] || name}</span>
        <div style={{ marginLeft: 'auto' }}>
          <Pill tone={tone}>{statusLabel(info?.status)}</Pill>
        </div>
      </div>
      {info?.latencyMs != null && info.latencyMs >= 0 && (
        <div className="nx-muted nx-mini">{info.latencyMs}ms response</div>
      )}
    </div>
  );
};

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [lastAt, setLastAt] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await get('/platform/health');
      setHealth(data);
      setLastAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e?.message || 'Failed to fetch health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const services = health?.services || {};
  const mem  = health?.system || {};
  const ver  = health?.version || {};
  // BUG-SH-4 FIX: error or null health must never show "all systems operational"
  const allOk = !error && health !== null &&
    Object.values(services).every(s => s.status === 'healthy' || s.status === 'not_configured');
  const heapPct  = mem.memoryHeapTotal ? Math.round((mem.memoryHeapUsed / mem.memoryHeapTotal) * 100) : 0;
  const hasDisk  = mem.diskTotalBytes > 0;
  const diskPct  = hasDisk ? Math.round((1 - mem.diskFreeBytes / mem.diskTotalBytes) * 100) : 0;
  const statCols = hasDisk ? 5 : 4;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="System Health"
        sub={lastAt ? 'Last refreshed at ' + lastAt + ' · auto-refresh 30s' : 'Loading…'}
        actions={
          <Btn variant={loading ? 'secondary' : 'primary'} icon="RefreshCw" onClick={refresh} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh Now'}
          </Btn>
        }
      />

      {/* Overall status banner */}
      <div className={'nx-health-banner' + (allOk ? ' is-ok' : ' is-warn')}>
        <NexusIcon name={allOk ? 'CheckCircle2' : 'AlertTriangle'} size={20} />
        <div>
          <span className="nx-health-banner-title">{getBannerTitle(loading, allOk)}</span>
          {health?.timestamp && (
            <span className="nx-health-banner-time">
              Uptime: {fmtUptime(health.uptime)} · Checked at {new Date(health.timestamp).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="nx-cc-chart-card" style={{ marginTop: 16, borderColor: 'var(--nx-danger)', background: '#FDECE8' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: 'var(--nx-danger)' }}>
            <NexusIcon name="AlertCircle" size={16} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Summary stats */}
      {health && (
        <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: `repeat(${statCols},1fr)`, marginTop: 16 }}>
          <StatCard label="Uptime"       value={fmtUptime(health.uptime)}                    icon="Clock"      color="#0E8C5A" />
          <StatCard label="Heap Used"    value={fmt(mem.memoryHeapUsed)}                     icon="Cpu"        color={heapPct > 80 ? '#E08A00' : '#125AB0'}
            sub={heapPct + '% of ' + fmt(mem.memoryHeapTotal)} />
          <StatCard label="RSS Memory"   value={fmt(mem.memoryRss)}                          icon="Server"     color="#125AB0" />
          {hasDisk && (
            <StatCard label="Disk Free"  value={fmt(mem.diskFreeBytes)}                      icon="HardDrive"  color={getDiskColor(diskPct)}
              sub={diskPct + '% used of ' + fmt(mem.diskTotalBytes)} />
          )}
          <StatCard label="API Response" value={(services.api?.latencyMs ?? '—') + 'ms'}    icon="Zap"        color={services.api?.latencyMs > 500 ? '#E08A00' : '#0E8C5A'} />
        </div>
      )}

      {/* Service grid */}
      <div style={{ marginTop: 20 }}>
        <div className="nx-cc-chart-head" style={{ marginBottom: 12 }}>
          <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NexusIcon name="Server" size={14} />Services
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {Object.entries(services).map(([name, info]) => (
            <ServiceCard key={name} name={name} info={info} />
          ))}
          {!health && !error && (
            Object.keys(SERVICE_ICONS).map(name => (
              <div key={name} className="nx-cc-chart-card nx-health-service-card nx-skeleton" style={{ height: 80 }} />
            ))
          )}
        </div>
      </div>

      {/* Version info */}
      {health && (
        <div className="nx-cc-chart-card" style={{ marginTop: 16 }}>
          <div className="nx-cc-chart-head">
            <span className="nx-cc-chart-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <NexusIcon name="Info" size={14} />Version Information
            </span>
          </div>
          <div className="nx-version-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', marginTop: 12 }}>
            {[
              { label: 'Application',  value: 'v' + (ver.app   || '2.0.0') },
              { label: 'Node.js',      value: ver.node   || '—'            },
              { label: 'Build',        value: ver.build  || '—'            },
            ].map(v => (
              <div key={v.label} style={{ textAlign: 'center', padding: '12px 0' }}>
                <div className="nx-muted nx-mini" style={{ marginBottom: 4 }}>{v.label}</div>
                <div style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{v.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
